import { BarangayTabulation, ChartData, MunicipalTabulation, PrecinctTabulation, Tabulation, VoterTrackStats } from '../../types';
import { Card, CardContent, Grid, Skeleton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import ThrowingStatsChart from './ThrowingStatsChart';
import { useNavigate } from 'react-router';

interface TabulationGraphProps {
  tabulation: Tabulation;
  stubReleasesStats: VoterTrackStats;
  finalReleasesStats: VoterTrackStats;
  exitPollsStats: VoterTrackStats;
  height: number;
  navUrl?: string;
}

const TabulationGraph: React.FC<TabulationGraphProps> = ({ 
  tabulation, 
  stubReleasesStats, 
  finalReleasesStats, 
  exitPollsStats, 
  height,
  navUrl 
}) => {
  const navigate = useNavigate();
  const [throwingStats, setThrowingStats] = useState<ChartData[]>([]);

  const getThrowingStats = (tabulation: Tabulation): ChartData[] => {
    const chartData: ChartData[] = [];

    const candidateVotes = tabulation.tally['DISTRICT 1 REPRESENTATIVE']['Lucille Sering'] || 0;

    if (tabulation.type === 'district') {
      chartData.push({
        name: "Polls",
        count: candidateVotes,
        value: (candidateVotes / tabulation.totalVoters) * 100
      });
      chartData.push({
        name: "Stub",
        count: stubReleasesStats.districtTotal,
        value: (stubReleasesStats.districtTotal / tabulation.totalVoters) * 100
      });
      chartData.push({
        name: "Throw",
        count: finalReleasesStats.districtTotal,
        value: (finalReleasesStats.districtTotal / tabulation.totalVoters) * 100
      });
      chartData.push({
        name: "Exit",
        count: exitPollsStats.districtTotal,
        value: (exitPollsStats.districtTotal / tabulation.totalVoters) * 100
      });
    }

    if (tabulation.type === 'municipal') {
      const tab = tabulation as MunicipalTabulation;
      const municipality_id = `${tab.city_municipality}_${tab.province}`;

      const stub = stubReleasesStats.municipalities[municipality_id] ?? 0
      const throwVal = finalReleasesStats.municipalities[municipality_id] ?? 0;
      const exit = exitPollsStats.municipalities[municipality_id] ?? 0;

      const stubPercent = (stub / tabulation.totalVoters) * 100;
      const throwPercent = (throwVal / tabulation.totalVoters) * 100;
      const exitPercent = (exit / tabulation.totalVoters) * 100;

      chartData.push({
        name: "Polls",
        count: candidateVotes,
        value: (candidateVotes / tabulation.totalVoters) * 100
      });
      chartData.push({
        name: "Stub",
        count: stub,
        value: stubPercent
      });
      chartData.push({
        name: "Throw",
        count: throwVal,
        value: throwPercent
      });
      chartData.push({
        name: "Exit",
        count: exit,
        value: exitPercent
      });
    }
    
    if (tabulation.type === 'barangay') {
      const tab = tabulation as BarangayTabulation;
      const barangay_id = `${tab.barangay}_${tab.city_municipality}_${tab.province}`;

      const stub = stubReleasesStats?.barangays[barangay_id] ?? 0
      const throwVal = finalReleasesStats?.barangays[barangay_id] ?? 0;
      const exit = exitPollsStats?.barangays[barangay_id] ?? 0;

      const stubPercent = (stub / tabulation.totalVoters) * 100;
      const throwPercent = (throwVal / tabulation.totalVoters) * 100;
      const exitPercent = (exit / tabulation.totalVoters) * 100;

      chartData.push({
        name: "Polls",
        count: candidateVotes,
        value: (candidateVotes / tabulation.totalVoters) * 100
      });
      chartData.push({
        name: "Stub",
        count: stub,
        value: stubPercent
      });
      chartData.push({
        name: "Throw",
        count: throwVal,
        value: throwPercent
      });
      chartData.push({
        name: "Exit",
        count: exit,
        value: exitPercent
      });
    }

    if (tabulation.type === 'precinct') {
      const tab = tabulation as PrecinctTabulation;
      const precinct_id = `${tab.precinct}_${tab.barangay}_${tab.city_municipality}_${tab.province}`;

      const stub = stubReleasesStats?.precincts[precinct_id] ?? 0
      const throwVal = finalReleasesStats?.precincts[precinct_id] ?? 0;
      const exit = exitPollsStats?.precincts[precinct_id] ?? 0;

      const stubPercent = (stub / tabulation.totalVoters) * 100;
      const throwPercent = (throwVal / tabulation.totalVoters) * 100;
      const exitPercent = (exit / tabulation.totalVoters) * 100;

      chartData.push({
        name: "Polls",
        count: candidateVotes,
        value: (candidateVotes / tabulation.totalVoters) * 100
      });
      chartData.push({
        name: "Stub",
        count: stub,
        value: stubPercent
      });
      chartData.push({
        name: "Throw",
        count: throwVal,
        value: throwPercent
      });
      chartData.push({
        name: "Exit",
        count: exit,
        value: exitPercent
      });
    }

    return chartData;
  };
  
  useEffect(() => {
    const f = async () => {
      const throwingStats = getThrowingStats(tabulation);
      setThrowingStats(throwingStats);
    };
    
    f();
  }, [tabulation]);
  
  return (
    <>
    {throwingStats.length > 0 ? (
      <Card 
        sx={{ 
          width: '100%', 
          cursor: navUrl ? 'pointer' : 'default' 
        }}
        onClick={navUrl ? () => {
          navigate(navUrl);
        } : undefined}
      >
        <CardContent>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="h5" textAlign="center" mb={2} fontWeight="bold">
                {tabulation.id}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <ThrowingStatsChart 
                chartData={throwingStats}
                height={height}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    ) : (
      <Skeleton variant="rectangular" width="100%" height={height} />
    )}
    </>
  );
};

export default TabulationGraph;
